import moment from 'moment';
import React, { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, MenuItem, Popover } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import { validateMinLength } from 'common/forms/formValidations';
import { updateDrugProfilesFilter } from 'store/thunks/drugThunks';

import { DrugProfileFieldKeys, DrugProfileStatus } from 'enums/drugManagement.enum';

const DrugsFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const filter = useSelector(({ drugs: { drugProfiles } }) => {
    let isActiveValue = DrugProfileStatus.ALL;
    if (drugProfiles.filter?.isActive !== undefined) {
      isActiveValue = drugProfiles.filter.isActive
        ? DrugProfileStatus.ACTIVE
        : DrugProfileStatus.INACTIVE;
    }

    return {
      ...drugProfiles.filter,
      ...(drugProfiles.filter?.createdAt && {
        createdAt: new Date(drugProfiles.filter?.createdAt),
      }),
      ...(drugProfiles.filter?.updatedAt && {
        updatedAt: new Date(drugProfiles.filter?.updatedAt),
      }),
      isActive: isActiveValue,
    };
  });

  const loading = useSelector(({ drugs }) => drugs.loading);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'DrugFilterButton-Popover' : undefined;

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    const { isActive, ...restFormData } = formData;
    const processedFilter = {
      ...restFormData,
      createdAt: formData?.createdAt && moment(formData.createdAt).utc(),
      updatedAt: formData?.updatedAt && moment(formData.updatedAt).utc(),
      isActive:
        isActive === DrugProfileStatus.ALL ? undefined : isActive === DrugProfileStatus.ACTIVE,
    };

    dispatch(
      updateDrugProfilesFilter({
        filter: processedFilter,
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>
      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={filter}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Drug Name'
                      name={DrugProfileFieldKeys.DRUG_NAME}
                      id='DrugFilterButton-drugName-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Created At'
                      name={DrugProfileFieldKeys.CREATED_AT}
                      id='DrugFilterButton-createdAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Strength'
                      name={DrugProfileFieldKeys.STRENGTH}
                      id='DrugFilterButton-strength-input'
                      validations={[validateMinLength('Minimum of 3 characters', 3)]}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Updated At'
                      name={DrugProfileFieldKeys.UPDATED_AT}
                      id='DrugFilterButton-updatedAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Status'
                      name={DrugProfileFieldKeys.STATUS}
                      id='DrugFilterButton-status-dropdown'
                    >
                      <MenuItem value={DrugProfileStatus.ALL}>All</MenuItem>
                      <MenuItem value={DrugProfileStatus.ACTIVE}>ACTIVE</MenuItem>
                      <MenuItem value={DrugProfileStatus.INACTIVE}>INACTIVE</MenuItem>
                    </SelectField>
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default DrugsFilterButton;
